import { DownOutlined } from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import {
  useDownloadAllPayroll,
  useGetPayroll,
  useSafeRecMultiplePayroll,
  useSendEmailPayslipMultiple,
  useSendPayslip,
  useVerifyPayslip,
  useViewPayslipById,
} from '@nxsys/hooks/api/payroll';
import usePayrollSocket from '@nxsys/hooks/usePayrollSocket';
import { buildTableExport } from './tableExport';
import { verifierColumn } from './verifierColumn';
import PayslipVerdictModal from './PayslipVerdictModal';
import { usePerformReversalPayrolls } from '@nxsys/hooks/api/timesheets';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { APP_NAME_BUREAU, IRELAND } from '@nxsys/store/constants/keys';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import {
  GetDraftPayrollListParams,
  GetPayrollListParams,
} from '@nxsys/store/slices/report/types';
import { Payroll } from '@nxsys/store/slices/revenueFilings/types';
import { PerformReversalPayrollsParams } from '@nxsys/types/api';
import {
  formatFullName,
  getTaxWeekTaxYear,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { trackPayslipZip } from '@nxsys/utils/payslip-zip-download';
import { formatMoney, formatMoneyExport } from '@nxsys/utils/number-helper';
import { Dropdown, Typography } from 'antd';
import { ExportToCsv } from 'export-to-csv';
import { debounce, sumBy } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TimesheetReverseModal from '../../../timesheets/components/TimesheetReverseModal';
import { useCanReversePayroll } from '@nxsys/hooks/useCanReversePayroll';
import FilterModal, { FilterValues } from '../../components/FilterModal';
import DownloadAllModal, {
  DownloadAllValues,
} from '../components/DownloadAllModal';
import {
  getPayrollCISColumns,
  getPayrollPayeUmbrellaColumns,
  hasSafeRecSetting,
  hasVeriPayeSetting,
} from './columns';
import { getTableScrollX } from './tableScrollX';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type PayrollSummaryReportProps = {
  paymentModels: PAYMENT_MODEL_ENUM[];
};

const PayrollSummaryReport: React.FC<PayrollSummaryReportProps> = ({
  paymentModels,
}) => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const isCisReportPage = paymentModels.includes(PAYMENT_MODEL_ENUM.CIS);
  const { isBureau, payrollCountry, user, isBureauEmployer } = useSelector(
    (state: AppState) => state.appStore,
  );
  const canReverse = useCanReversePayroll();
  const isIreland = payrollCountry === IRELAND;
  // Ireland's models depend on the product too: bureau has PAYE + RCT, umbrella
  // adds UMBRELLA. Hardcoding the bureau pair here filtered every Irish
  // umbrella payroll out of this surface's counts and lists.
  const validPaymentModels = isIreland
    ? getIrelandPaymentModels(isBureau)
    : isBureau
      ? [PAYMENT_MODEL_ENUM.PAYEE]
      : isCisReportPage
        ? [PAYMENT_MODEL_ENUM.CIS, PAYMENT_MODEL_ENUM.NON_CIS]
        : [
            PAYMENT_MODEL_ENUM.UMBRELLA,
            PAYMENT_MODEL_ENUM.PAYEE,
            PAYMENT_MODEL_ENUM.HYBRID,
            PAYMENT_MODEL_ENUM.PAYE_HYBRID,
            PAYMENT_MODEL_ENUM.PAYE_FTH,
          ];

  const dispatch = useDispatch();
  const { payrollList } = useSelector((state: AppState) => state.reportStore);
  const { list, pagination, loading, queryParams } = payrollList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<Payroll[]>([]);
  // NX-4971 — advisory payslip verifier (view verdict detail / re-run).
  const [verdictRecord, setVerdictRecord] = useState<Payroll | null>(null);
  const [rerunningIds, setRerunningIds] = useState<string[]>([]);
  const { run: verifyPayslip, loading: isVerifyLoading } =
    useVerifyPayslip();
  const displayList = useMemo(
    () =>
      (list ?? []).map((row: Payroll) =>
        rerunningIds.includes(row.id)
          ? {
              ...row,
              payslipVerificationRef: {
                ...row.payslipVerificationRef,
                status: 'RUNNING' as const,
                verdict: null,
              },
            }
          : row,
      ),
    [list, rerunningIds],
  );
  const handleViewVerdict = (record: Payroll) => setVerdictRecord(record);
  const handleRerunVerification = (record: Payroll) => {
    setVerdictRecord(null);
    setRerunningIds((prev) =>
      prev.includes(record.id) ? prev : [...prev, record.id],
    );
    verifyPayslip({ payrollIds: [record.id] })
      .then((res) => {
        if (res?.success) {
          showMessage({
            type: 'info',
            content:
              'Re-verification requested. The verdict will update shortly.',
          });
        } else {
          setRerunningIds((prev) => prev.filter((id) => id !== record.id));
        }
      })
      .catch(() => {
        setRerunningIds((prev) => prev.filter((id) => id !== record.id));
      });
  };
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);
  const [reverseModal, setReverseModal] = useState<{
    isOpen: boolean;
    payrollIds: string[];
  }>({
    isOpen: false,
    payrollIds: [],
  });
  const toggleReverseModal = () => {
    setReverseModal((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  };

  const { run: getPayroll, loading: isGetPayrollLoading } = useGetPayroll();
  const { goBack } = useGoBackByQuery();

  const handleGoBack = useCallback(
    () =>
      goBack({
        fromPathQueryKey: 'from',
        fallbackPathDefault: '/reports',
      }),
    [goBack],
  );

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={
            isCisReportPage
              ? messages['common.cisPaymentSummary']
              : isBureau
                ? isIreland
                  ? 'Payment Summary'
                  : messages['common.paymentSummaryPaye']
                : messages['common.paymentSummaryReportPayeeUmbrella']
          }
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, isCisReportPage, handleGoBack]);

  const renderRightContent = () => {
    const menuProps = {
      items: [
        ...(!isBureau && showSafeRec
          ? [
              {
                key: 'safeRec',
                label: messages['common.sendToSafeRec'],
                onClick: handleSafeRecSelected,
              },
            ]
          : []),
        ...(canReverse
          ? [
              {
                key: 'reverse',
                label: messages['timesheet.reverse'],
                onClick: handleOpenReverseSelected,
              },
            ]
          : []),
        {
          key: 'sendEmailPayslip',
          label: messages['common.sendEmailPayslip'],
          onClick: handleSendEmailPayslipSelected,
        },
        // NX-4971 — advisory bulk verify; super-admin only, mirroring the Verifier column.
        ...(user?.isSuperAdmin
          ? [
              {
                key: 'verifyPayslips',
                label: messages['common.verifyPayslips'],
                onClick: handleVerifyPayslipSelected,
              },
            ]
          : []),
        {
          key: 'export',
          label: messages['common.export'],
          onClick: handleExportSelected,
        },
        {
          key: 'exportAll',
          label: messages['common.exportAll'],
          onClick: handleExportAll,
        },
        {
          key: 'downloadAll',
          label: messages['common.downloadAll'],
          onClick: () => setIsOpenDownloadAllModal(true),
        },
      ],
    };
    return (
      <Dropdown menu={menuProps}>
        <AppButton
          type="primary"
          name={messages['common.action']}
          rightIcon={<DownOutlined />}
        />
      </Dropdown>
    );
  };

  const handleFetchData = useCallback(
    (params: GetPayrollListParams) => {
      const newParams = {
        ...params,
        paymentModels: validPaymentModels,
        appName: isBureau ? APP_NAME_BUREAU : undefined,
      };

      newParams.companyRefs = resolveFilterCompanyIds(
        params.companyRefs,
        isBureauEmployer,
        user?.companyUserIds,
      );

      dispatch(reportActions.getPayrollList(newParams));
    },
    [
      dispatch,
      isBureau,
      isBureauEmployer,
      user?.companyUserIds,
      validPaymentModels,
    ],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };
  usePayrollSocket({
    eventName: `payslip-verify-${user?.id}`,
    onSuccess: () => {
      setRerunningIds([]);
      refetchData();
    },
  });

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: FilterValues) => {
    const params: GetDraftPayrollListParams = {
      ...queryParams,
      paymentStartDate: values.paymentDateFrom
        ? values.paymentDateFrom.format('MM/DD/YYYY')
        : undefined,
      paymentEndDate: values.paymentDateTo
        ? values.paymentDateTo.format('MM/DD/YYYY')
        : undefined,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
      batchFrom: values.batchFrom,
      batchTo: values.batchTo,
      companyRefs: values.companies?.map((i) => i.value),
      safeRecStatuses: values.safeRecStatuses?.map((i) => i.value),
      veriPayeStatuses: values.veriPayeStatuses?.map((i) => i.value),
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const totalTimesheetValuesSelected = selectedRows.length
    ? sumBy(selectedRows, (item: Payroll) => Number(item.timesheetValue))
    : 0;

  const handleExport = (data: Payroll[]) => {
    if (isCisReportPage) {
      handleExportCIS(data);
      return;
    }
    // Built from the table's own columns so the file matches the screen — and,
    // since Draft Payroll shows these same columns, matches the Draft Payroll
    // export too (NX-5791).
    const { headers, dataExport } = buildTableExport(data, tableColumns);
    exportCsv('Payroll-export', headers, dataExport);
  };

  const handleExportCIS = (data: Payroll[]) => {
    const cs = showCurrencyByCountry(payrollCountry);
    const fm = (v: any) => (v != null ? formatMoneyExport(Number(v), cs) : '');
    const fd = (v: any) =>
      v ? moment(v).tz('Europe/London').format('DD/MM/YYYY') : '';
    const ref = (sub: any) =>
      `${sub?.departmentPrefix || sub?.primaryAgency?.agencyPrefix || '___'}/${sub?.agencyRef || ''}`;

    const dataExport = data.map((item) => ({
      timesheetId: item?.timeSheetRef?.id || '',
      employeeId: item?.subcontractorRef?.id || '',
      agencyRef: ref(item?.subcontractorRef),
      agencyName: item?.agencyName || '',
      paymentCompany: item?.companyRef?.companyName || '',
      employeeName: formatFullName(item?.subcontractorRef),
      taxPeriod: String(item?.taxPeriod || ''),
      taxYear: String(item?.taxYear || ''),
      timesheetValue: fm(item?.timesheetValue),
      totalHours: String(item?.payeeUmbrellaValue?.totalHours ?? ''),
      margin: fm(item?.margin),
      gross: fm(item?.gross),
      materials: fm(item?.materials),
      vat: fm(item?.vat),
      net: fm(item?.net),
      cisStatus: item?.cisStatus || '',
      cisDeduction: fm(item?.cisDeduction),
      otherPayment: fm(item?._otherPayment),
      otherDeduction: fm(Math.abs(Number(item?._otherDeduction) || 0)),
      paymentDate: fd(item?.paymentDate),
      selfBillId: item?.selfBillRef?.id || '',
      selfBillDate: fd(item?.selfBillRef?.billDate),
      salesInvRef: item?.invoiceNo || '',
    }));
    const headers = [
      'Timesheet ID',
      'Employee ID',
      isBureau ? 'Employer Ref' : 'Agency Ref',
      'Agency Name',
      'Payment Company',
      'Employee Name',
      'Tax Period',
      'Tax Year',
      'Timesheet Values',
      'Hours',
      'Margin',
      'Gross',
      'Materials',
      'VAT',
      'Net',
      'CIS Status',
      'CIS Deduction',
      'Other Payment',
      'Other Deduction',
      'Payment Date',
      'Self Bill ID',
      'Self Bill Date',
      'Sales Inv Ref',
    ];
    exportCsv('Payroll-export', headers, dataExport);
  };

  const exportCsv = (prefix: string, headers: string[], dataExport: any[]) => {
    const csvExporter = new ExportToCsv({
      filename: `${prefix}-${moment(new Date()).tz('Europe/London').format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getPayroll({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
      paymentModels: validPaymentModels,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  const [isOpenDownloadAllModal, setIsOpenDownloadAllModal] = useState(false);
  const { run: downloadAllPayroll, loading: isDownloadAllPayrollLoading } =
    useDownloadAllPayroll();
  const handleDownloadAll = async (values: DownloadAllValues) => {
    const res = await downloadAllPayroll({
      companyPaymentId: values.companyPayment?.value || '',
      agencyId: values.agency?.value || '',
      taxYear: values.taxYear,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
      paymentModels: validPaymentModels,
    });
    if (res.success) {
      trackPayslipZip(res.data.jobId, res.data.total);
    }
  };

  const {
    run: sendEmailPayslipMultiple,
    loading: isSendEmailPayslipMultipleLoading,
  } = useSendEmailPayslipMultiple();
  const handleSendEmailPayslipMultiple = (data: Payroll[]) => {
    sendEmailPayslipMultiple(data.map((item) => item.id)).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.emailSentPayslipPending'],
        });
      }
    });
  };
  const handleSendEmailPayslipSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleSendEmailPayslipMultiple(selectedRows);
  };

  // NX-4971 — bulk queue advisory verification for the selected payslips (same
  // endpoint as the per-row re-run, just an array of ids).
  const {
    run: verifyPayslipMultiple,
    loading: isVerifyPayslipMultipleLoading,
  } = useVerifyPayslip();
  const handleVerifyPayslipMultiple = (data: Payroll[]) => {
    verifyPayslipMultiple({ payrollIds: data.map((item) => item.id) }).then(
      (res) => {
        if (res?.success) {
          const cisSkipped = (res.skipped ?? []).filter(
            (s) => s.reason === 'not_paye_umbrella',
          ).length;
          const content = cisSkipped
            ? `${res.queued ?? 0} ${messages['common.verifyPayslipQueued']} · ${cisSkipped} ${messages['common.verifyPayslipSkippedCis']}`
            : `${res.queued ?? 0} ${messages['common.verifyPayslipQueued']}`;
          showMessage({
            type: 'success',
            content,
          });
        }
      },
    );
  };
  const handleVerifyPayslipSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleVerifyPayslipMultiple(selectedRows);
  };
  useNotificationSocket({
    eventName: `confirm-payments-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.confirmPaymentSuccess'],
      });
      refetchData();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.confirmPaymentFailed'],
      });
    },
  });
  useNotificationSocket({
    eventName: `send-mail-self-bill-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.emailSentPayslipSuccess'],
      });
      refetchData();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.emailSentPayslipFailed'],
      });
    },
  });
  useNotificationSocket({
    eventName: `reversal-payroll-from-timesheet-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.reversalPayrollSuccess'],
      });
      setSelectedRows([]);
      refetchData();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.reversalPayrollFailed'],
      });
    },
  });
  useNotificationSocket({
    eventName: `reversal-payroll-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.reversalPayrollSuccess'],
      });
      setSelectedRows([]);
      refetchData();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.reversalPayrollFailed'],
      });
    },
  });

  const { run: viewPayslipById, loading: isViewPayslipByIdLoading } =
    useViewPayslipById();
  const handleViewPayslipById = (id: string) => {
    viewPayslipById(id).then((res) => {
      if (res.success) {
        window.open(res.data, '_blank');
      }
    });
  };

  const { run: sendPayslip, loading: isSendPayslipLoading } = useSendPayslip();
  const handleSendPayslip = (data: Payroll) => {
    const params = {
      id: data.selfBillRef?.id || '',
      companyId: data.companyRef?.id || '',
      subcontractorId: data.subcontractorRef?.id || '',
    };
    sendPayslip(params).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.emailSentPayslipSuccess'],
        });
        refetchData();
      }
    });
  };

  const {
    run: safeRecMultiplePayroll,
    loading: isSafeRecMultiplePayrollLoading,
  } = useSafeRecMultiplePayroll();
  const handleSafeRecMultiplePayroll = (data: Payroll[]) => {
    const emptySelfBillPayroll = data.filter((pr) => !pr?.selfBillRef);

    if (emptySelfBillPayroll.length) {
      showMessage({
        type: 'error',
        content: `Payrolls ${emptySelfBillPayroll
          .map((item) => item?.id)
          .join(',')} do not have SelfBill`,
      });
      return;
    }

    safeRecMultiplePayroll(data.map((item) => item.id)).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.safeRecPayrollSuccess'],
        });
        refetchData();
      }
    });
  };
  const handleSafeRecPayroll = (data: Payroll) => {
    handleSafeRecMultiplePayroll([data]);
  };
  const handleViewSafeRecSubmission = useCallback(
    (data: Payroll) => {
      navigate(`/reports/payroll/safe-rec-submission/${data.id}`, {
        query: {
          from: isCisReportPage
            ? '/reports/payroll/cis-payment-summary'
            : '/reports/payroll/payment-summary',
        },
      });
    },
    [navigate, isCisReportPage],
  );
  const handleSafeRecSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleSafeRecMultiplePayroll(selectedRows);
  };

  const {
    run: performReversalPayrolls,
    loading: isPerformReversalPayrollsPending,
  } = usePerformReversalPayrolls();
  const handleReverse = async (params: PerformReversalPayrollsParams) => {
    await performReversalPayrolls({
      ...params,
      isReportCIS: isCisReportPage,
    }).then(() => {
      showMessage({
        type: 'info',
        content: messages['timesheet.reverseSuccessPending'],
      });
    });
  };
  const handleOpenReverse = (data: Payroll[]) => {
    setReverseModal((prev) => ({
      ...prev,
      isOpen: true,
      payrollIds: data.map((item) => item.id),
    }));
  };
  const handleOpenReverseSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleOpenReverse(selectedRows);
  };

  const handleView = useCallback(
    (id: string) => {
      navigate(`/reports/payroll/${id}`, {
        query: {
          from: isCisReportPage
            ? '/reports/payroll/cis-payment-summary'
            : '/reports/payroll/payment-summary',
        },
      });
    },
    [navigate],
  );

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.paymentStartDate ||
    !!queryParams?.paymentEndDate ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.batchFrom ||
    !!queryParams?.batchTo ||
    (queryParams?.companyRefs && queryParams.companyRefs.length > 0) ||
    (queryParams?.safeRecStatuses && queryParams.safeRecStatuses.length > 0) ||
    (queryParams?.veriPayeStatuses && queryParams.veriPayeStatuses.length > 0);

  // Keep a column only while at least one row's company has the integration;
  // each cell then still checks its own row, since the listing mixes companies.
  const showSafeRec = useMemo(
    () => (list ?? []).some(hasSafeRecSetting),
    [list],
  );
  const showVeriPaye = useMemo(
    () => (list ?? []).some(hasVeriPayeSetting),
    [list],
  );

  const tableColumns = useMemo(() => {
    const base = isCisReportPage
      ? getPayrollCISColumns({
          messages,
          payrollCountry,
          handleViewPayslipById,
          handleSendPayslip,
          handleSafeRecPayroll,
          handleViewSafeRecSubmission,
          handleOpenReverse,
          handleView,
          isBureau,
          hideReverse: !canReverse,
        })
      : getPayrollPayeUmbrellaColumns({
          messages,
          payrollCountry,
          handleViewPayslipById,
          handleSendPayslip,
          handleSafeRecPayroll,
          handleViewSafeRecSubmission,
          handleOpenReverse,
          handleView,
          isBureau,
          hideReverse: !canReverse,
          showSafeRec,
          showVeriPaye,
        });
    // Verifier sits right AFTER the Action column (index 0). Super-admin only.
    if (user?.isSuperAdmin) {
      base.splice(
        1,
        0,
        verifierColumn(messages, handleViewVerdict as any) as any,
      );
    }
    return base;
  }, [
    isCisReportPage,
    messages,
    payrollCountry,
    handleViewPayslipById,
    handleSendPayslip,
    handleSafeRecPayroll,
    handleViewSafeRecSubmission,
    handleOpenReverse,
    handleView,
    isBureau,
    canReverse,
    showSafeRec,
    showVeriPaye,
    user?.isSuperAdmin,
    handleViewVerdict,
  ]);

  const tableScrollX = useMemo(
    () => getTableScrollX(tableColumns),
    [tableColumns],
  );

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isGetPayrollLoading ||
        isDownloadAllPayrollLoading ||
        isSendEmailPayslipMultipleLoading ||
        isVerifyPayslipMultipleLoading ||
        isViewPayslipByIdLoading ||
        isSendPayslipLoading ||
        isSafeRecMultiplePayrollLoading ||
        isPerformReversalPayrollsPending
      }
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              {selectedRows.length > 0 && (
                <Typography.Text>
                  {messages['common.totalTimesheetValue']}:{' '}
                  {formatMoney(
                    totalTimesheetValuesSelected,
                    showCurrencyByCountry(payrollCountry),
                  )}
                </Typography.Text>
              )}
              <TaxYearSelect
                initValue={currentTaxYear}
                onFilter={(value) =>
                  handleFetchData({ ...queryParams, taxYear: value.taxYear })
                }
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={displayList}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: tableScrollX, y: 700 }}
            columns={tableColumns}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <FilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
        isBureau={isBureau}
      />
      <DownloadAllModal
        isOpen={isOpenDownloadAllModal}
        toggle={() => setIsOpenDownloadAllModal(!isOpenDownloadAllModal)}
        onDownload={handleDownloadAll}
        initialValues={{
          taxYear: queryParams?.taxYear ?? currentTaxYear,
        }}
      />
      <TimesheetReverseModal
        isOpen={reverseModal.isOpen}
        toggle={toggleReverseModal}
        payrollIds={reverseModal.payrollIds}
        onPerform={handleReverse}
      />
      <PayslipVerdictModal
        isOpen={!!verdictRecord}
        toggle={() => setVerdictRecord(null)}
        record={verdictRecord as any}
        onRerun={handleRerunVerification as any}
        rerunLoading={isVerifyLoading}
        onSaved={refetchData}
      />
    </AppsContainer>
  );
};

export default PayrollSummaryReport;
